import { keys, pickBy } from "lodash";
import { createSelector } from "reselect";
// eslint-disable-next-line @logicsoftware/ep-custom/no-cross-feature-imports
import { getCurrentUserGlobalRoleId } from "~/app/selectors/common";
import { SNAPSHOT_ROWS_LIMIT } from "../const";
import {
    ActualCompletionDateEditPermissions,
    CustomFieldsPermissions,
    CustomizationsState,
    CustomizationState,
    GenerateFlavorReportConfig,
    InternalHourlyRatePermissions,
    PortfoliosPermissionsConfig,
    TasksDeactivationConfig,
} from "../types";

export const getIsCustomizationLoaded = (state: CustomizationsState) => !!state.customization.__isCustomizationLoaded;

export const getFeatureFlags = (state: CustomizationsState) => state.customization;

export const getActiveCustomizations = createSelector(getFeatureFlags, customization =>
    keys(pickBy(customization, (v, k) => k !== "__isCustomizationLoaded" && v === true))
);

export const getIsIntegrationsEnabled = createSelector(
    getFeatureFlags,
    customization => customization.ClientIsBuiltInIntegrationsEnabled !== false
);

export const getIsIntercomForGuestsDisabled = (state: CustomizationsState) =>
    state.customization.IsIntercomForGuestsDisabled;
export const getIsIntercomDisabled = (state: CustomizationsState) => state.customization.IsIntercomDisabled;

export const getIsNewReportsEngineEnabled = (state: CustomizationsState) =>
    state.customization.IsNewReportsEngineEnabled;

export const getSnapshotRowsLimit = (state: CustomizationsState) =>
    state.customization.SnapshotRowsLimit ?? SNAPSHOT_ROWS_LIMIT;

export const getClientIsLtkFinMetricsHidden = (state: CustomizationsState) =>
    state.customization.ClientIsLtkFinMetricsHidden;

export const getClientIsJobRolesEnabled = (state: CustomizationsState) => state.customization.ClientIsJobRolesEnabled;

export const getIsOldRLHoursAllocationEnabled = (state: CustomizationsState) =>
    state.customization?.IsOldRLHoursAllocationEnabled;

export const getClientIsGroupByPortfolioEnabled = (state: CustomizationsState) =>
    state.customization.ClientIsGroupByPortfolioEnabled;

export const getClientIsNestedPortfoliosEnabled = (state: CustomizationsState) =>
    state.customization.ClientIsNestedPortfoliosEnabled;

/**
 * Indicates that row with "Add task" btn should be shown on the
 * new activity center grid after last task from project.
 */
export const getIsAcCreateTaskRowEnabled = (state: CustomizationsState) =>
    !!state.customization?.ClientIsAcCreateTaskRowEnabled;

export const getClientIsPasswordUpdatesDisabled = (state: CustomizationsState) =>
    state?.customization?.ClientIsPasswordUpdatesDisabled;

export const getClientIsEmailUpdatesDisabled = (state: CustomizationsState) =>
    state?.customization?.ClientIsEmailUpdatesDisabled;

export const getIsProfitabilityMetricsVisible = (state: CustomizationsState) =>
    state.customization?.IsProfitabilityMetricsVisible;

export const getClientIsUnnamedResourcesEnabled = (state: CustomizationsState) =>
    state.customization?.ClientIsUnnamedResourcesEnabled !== false;

export const getClientIsPlanningResourcesEnabled = (state: CustomizationsState) =>
    state.customization?.ClientIsPlanningResourcesEnabled;

export const getClientIsSampleDataSetEnabled = (state: CustomizationsState) =>
    !!state.customization?.ClientIsSampleDataSetEnabled;

export const getIsFormCustomRulesEnabled = (state: CustomizationsState) =>
    state.customization?.IsFormCustomRulesEnabled;

export const getClientIsReactFormsEnabled = (state: CustomizationsState) =>
    state.customization?.ClientIsReactFormsEnabled;

export const getClientIsAITasksGenerationEnabled = (state: CustomizationsState) =>
    state.customization?.ClientIsAITasksGenerationEnabled !== false;

export const getClientIsSpacesEnabled = (state: CustomizationsState) => !!state.customization?.ClientIsSpacesEnabled;

export const getClientUserFlowEnabled = (state: CustomizationsState) => false; // !!state.customization?.ClientUserFlowEnabled;

export const getAdvancedFileSearch = (state: CustomizationsState) => state.customization.AdvancedFileSearch;
export const getAdvancedActivitiesSearch = (state: CustomizationsState) => state.customization.AdvancedActivitiesSearch;
export const getAdvancedPortfolioSearch = (state: CustomizationsState) => state.customization.AdvancedPortfolioSearch;
export const getAdvancedProjectsSearch = (state: CustomizationsState) => state.customization.AdvancedProjectsSearch;
export const getClientIsPennLongBranchEnabled = (state: CustomizationsState) =>
    state.customization.ClientIsPennLongBranchEnabled;

export const getCustomFieldsPermissions = createSelector(
    getFeatureFlags,
    (customization): CustomFieldsPermissions =>
        customization?.CustomFieldsPermissions ? JSON.parse(customization.CustomFieldsPermissions) : null
);

export const getTasksDeactivationConfigCombiner = (customization: CustomizationState): TasksDeactivationConfig =>
    customization?.TasksDeactivationConfigJson ? JSON.parse(customization.TasksDeactivationConfigJson) : null;

export const getTasksDeactivationConfig = createSelector(getFeatureFlags, getTasksDeactivationConfigCombiner);

export const getIsGenerateFlavorReportAvailable = createSelector(
    getFeatureFlags,
    getCurrentUserGlobalRoleId,
    (customization, roleId) =>
        (
            (customization?.ClientGenerateFlavorReportConfig
                ? JSON.parse(customization.ClientGenerateFlavorReportConfig)
                : null) as GenerateFlavorReportConfig
        )?.AccessLevelsAllowedToGenerate?.includes(roleId) ?? false
);

export const getActualCompletionDateEditPermissions = createSelector(
    getFeatureFlags,
    (customization): ActualCompletionDateEditPermissions => {
        return customization?.ActualCompletionDateEditPermissions
            ? JSON.parse(customization.ActualCompletionDateEditPermissions)
            : null;
    }
);

export const getRestrictDatesEditing = createSelector(
    getActualCompletionDateEditPermissions,
    getCurrentUserGlobalRoleId,
    (permissions, roleId): boolean => {
        if (permissions == null) {
            return false;
        }

        const accessLevelIds = permissions.AccessLevelsAllowedToEdit;
        const allowedToEdit = accessLevelIds.includes(roleId);

        return !allowedToEdit;
    }
);

export const getRestrictParentDatesEditing = createSelector(
    getActualCompletionDateEditPermissions,
    getCurrentUserGlobalRoleId,
    (permissions, roleId): boolean => {
        if (permissions == null) {
            return false;
        }

        const accessLevelIds = permissions.AccessLevelsAllowedToEdit;
        const allowedToEdit = accessLevelIds.includes(roleId);

        const accessLevelDeniedToEditParentIds = permissions.AccessLevelsDeniedToEditParents;
        const deniedToEditParents = accessLevelDeniedToEditParentIds?.includes(roleId);

        return !allowedToEdit || deniedToEditParents;
    }
);

export const getRestrictInternalHourlyRateAccess = createSelector(
    getFeatureFlags,
    getCurrentUserGlobalRoleId,
    (customization, roleId): boolean => {
        const permissions: InternalHourlyRatePermissions = customization?.InternalHourlyRatePermissions
            ? JSON.parse(customization.InternalHourlyRatePermissions)
            : null;

        if (permissions == null) {
            return false;
        }

        const accessLevelIds = permissions.AccessLevelsAllowedToView;
        const allowedToView = accessLevelIds.includes(roleId);

        return !allowedToView;
    }
);

export const getTimeEntriesSubmission = (state: CustomizationsState) => state.customization.TimeEntriesSubmition;

export const getAllowedToDeletePortfolio = createSelector(
    getFeatureFlags,
    getCurrentUserGlobalRoleId,
    (customization, roleId): boolean => {
        const permissions: PortfoliosPermissionsConfig = customization?.PortfoliosPermissionsConfig
            ? JSON.parse(customization.PortfoliosPermissionsConfig)
            : null;

        if (permissions == null) {
            return true;
        }

        const accessLevelIds = permissions.AccessLevelsAllowedToDelete;

        return accessLevelIds.includes(roleId);
    }
);
